import { c as _c } from "react/compiler-runtime";
import { AddressAutocomplete } from "@components/address-autocomplete/address-autocomplete";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { CircularProgress, EditIcon, Text, Tooltip } from "@flat-ui";
import { Box, ClickAwayListener, Stack } from "@mui-wrapper";
import { UiUtils } from "@stellar/utils";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { getProjectAddressType } from "@utils/project-utils";
import { parseCoordinatesString } from "@utils/string-utils";
import { ChangeProjectDetailsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useRef, useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";
import { withEllipsisTwoLines } from "@styles/common-styles";

/** Payload to update the project address and GPS position fields */

/**
 * @returns A component that displays the project address.
 * If the project is editable, it allows the user to edit the address.
 * Saves automatically when the user selects a geocoded suggestion, presses Enter, or clicks away.
 * Cancels without saving when the user presses Escape or exits without making changes.
 */
export function ProjectAddress(t0) {
  const $ = _c(51);
  const {
    project,
    isProjectEditable
  } = t0;
  const dispatch = useAppDispatch();
  const {
    trackEvent
  } = useTrackEvent();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const addressRef = useRef(null);
  const [input, setInput] = useState(project.address);
  let t1;
  if ($[0] !== project.address) {
    t1 = function enterEditMode() {
      setIsEditMode(true);
      setIsUpdating(false);
      setInput(project.address);
    };
    $[0] = project.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const enterEditMode = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function exitEditMode() {
      setIsEditMode(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const exitEditMode = t2;
  let t3;
  if ($[3] !== dispatch || $[4] !== project.id || $[5] !== project.position || $[6] !== trackEvent) {
    t3 = async function updateAddress(payload) {
      const updatedPosition = payload.position === undefined ? project.position : payload.position;
      trackEvent({
        name: ChangeProjectDetailsEvents.changeProjectAddress,
        props: {
          addressType: getProjectAddressType({
            address: payload.address,
            postalAddress: payload.postalAddress,
            emptyAddressType: "removed"
          }),
          isGpsPositionSet: Boolean(updatedPosition)
        }
      });
      setIsUpdating(true);
      await dispatch(updateProjectDetails({
        projectId: project.id,
        payload
      }));
      setIsUpdating(false);
      exitEditMode();
    };
    $[3] = dispatch;
    $[4] = project.id;
    $[5] = project.position;
    $[6] = trackEvent;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const updateAddress = t3;
  let t4;
  if ($[8] !== updateAddress) {
    t4 = function onGeocodedAddressSelect(geocodedAddress) {
      setInput(geocodedAddress.address);
      updateAddress({
        address: geocodedAddress.address,
        position: geocodedAddress.position,
        postalAddress: geocodedAddress.postalAddress
      });
    };
    $[8] = updateAddress;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onGeocodedAddressSelect = t4;
  let t5;
  if ($[10] !== project.address || $[11] !== updateAddress) {
    t5 = function onCustomAddressSelect(value) {
      let address = value.trim();
      let position = undefined;
      if (address === project.address) {
        exitEditMode();
        return;
      }
      if (address.length === 0) {
        address = UiUtils.DASH;
        position = null;
      }
      const parsedCoordinates = parseCoordinatesString(address);
      if (parsedCoordinates) {
        position = parsedCoordinates;
      }
      updateAddress({
        address,
        position,
        postalAddress: null
      });
    };
    $[10] = project.address;
    $[11] = updateAddress;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onCustomAddressSelect = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      mb: "30px"
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Text variant="bodyS" sx={{
      color: "text.secondary"
    }}>Address</Text>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== input || $[16] !== isEditMode || $[17] !== onCustomAddressSelect) {
    t8 = () => {
      if (isEditMode) {
        onCustomAddressSelect(input);
      }
    };
    $[15] = input;
    $[16] = isEditMode;
    $[17] = onCustomAddressSelect;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t10;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsHovered(true);
    t10 = () => setIsHovered(false);
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  let t11;
  if ($[21] !== enterEditMode || $[22] !== isEditMode || $[23] !== isProjectEditable) {
    t11 = () => {
      if (isProjectEditable && !isEditMode) {
        enterEditMode();
      }
    };
    $[21] = enterEditMode;
    $[22] = isEditMode;
    $[23] = isProjectEditable;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = isProjectEditable ? "pointer" : "default";
  let t13;
  if ($[25] !== t12) {
    t13 = {
      cursor: t12,
      justifyContent: "space-between",
      minHeight: "39px",
      alignItems: "center"
    };
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== input || $[28] !== isEditMode || $[29] !== isTooltipOpen || $[30] !== isUpdating || $[31] !== onCustomAddressSelect || $[32] !== onGeocodedAddressSelect || $[33] !== project.address) {
    t14 = isEditMode ? <AddressAutocomplete input={input} setInput={setInput} onGeocodedAddressSelect={onGeocodedAddressSelect} onCustomAddressSelect={onCustomAddressSelect} onLoadingGeocodedAddressChange={state => {
      if (state === "rejected") {
        exitEditMode();
      }
    }} onCancel={exitEditMode} variant="standard" isDisabled={isUpdating} /> : <Tooltip title={<var>{project.address}</var>} open={isTooltipOpen} onOpen={() => {
      const address_0 = addressRef.current;
      setIsTooltipOpen(!!address_0 && address_0.scrollHeight > address_0.clientHeight);
    }} onClose={() => setIsTooltipOpen(false)}><Text ref={addressRef} variant="bodyM" sx={{
        ...withEllipsisTwoLines,
        minWidth: 0
      }}><var>{project.address || UiUtils.DASH}</var></Text></Tooltip>;
    $[27] = input;
    $[28] = isEditMode;
    $[29] = isTooltipOpen;
    $[30] = isUpdating;
    $[31] = onCustomAddressSelect;
    $[32] = onGeocodedAddressSelect;
    $[33] = project.address;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== enterEditMode || $[36] !== isEditMode || $[37] !== isHovered || $[38] !== isProjectEditable) {
    t15 = isProjectEditable && isHovered && !isEditMode && <SphereIconButton component={EditIcon} buttonSize="24px" iconSize="16px" onClick={enterEditMode} color={PALETTE_VARS.text.disabled} />;
    $[35] = enterEditMode;
    $[36] = isEditMode;
    $[37] = isHovered;
    $[38] = isProjectEditable;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== isUpdating) {
    t16 = isUpdating && <CircularProgress size={16} />;
    $[40] = isUpdating;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t11 || $[43] !== t13 || $[44] !== t14 || $[45] !== t15 || $[46] !== t16) {
    t17 = <Stack direction="row" onMouseEnter={t9} onMouseLeave={t10} onClick={t11} sx={t13}>{t14}{t15}{t16}</Stack>;
    $[42] = t11;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t17 || $[49] !== t8) {
    t18 = <Box sx={t6}>{t7}<ClickAwayListener onClickAway={t8}>{t17}</ClickAwayListener></Box>;
    $[48] = t17;
    $[49] = t8;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
}